import { NotesPanel } from "./NotesPanel";
import { monthBounds } from "../lib/notes";
import { useMemo } from "react";
import { Printer, FileText } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ChartTooltip } from "./ChartTooltip";
import { QuarterCompare } from "./QuarterCompare";
import { PrintHeader } from "./PrintHeader";
import { HistoryCompare } from "./HistoryCompare";
import { HeadcountEfficiency } from "./HeadcountEfficiency";
import { teamMonthly, quarterReview, lastDataDay, fullMonths, peakGapHour } from "../lib/summary";
import { DEFAULT_SHEET, toQuarters, pctChange, fmtDur } from "../lib/pickerSheet";
import { monthLabel } from "../lib/csvAnalysis";
import { fmtNum, fmtCompact } from "../lib/format";

const Delta = ({ v, inverse }) => {
  if (v == null) return <span className="text-xs text-slate-600">—</span>;
  const good = inverse ? v <= 0 : v >= 0;
  return <span className={`text-xs font-mono ${good ? "text-emerald-400" : "text-rose-400"}`}>{v >= 0 ? "▲" : "▼"} {Math.abs(v).toFixed(1)}%</span>;
};

const Kpi = ({ label, value, d, inverse, testId }) => (
  <div className="panel p-4" data-testid={testId}>
    <p className="label-mono">{label}</p>
    <p className="mt-3 font-mono text-2xl text-slate-100">{value}</p>
    <div className="mt-1"><Delta v={d} inverse={inverse} /></div>
  </div>
);

const List = ({ title, rows, render, testId }) => (
  <div className="panel p-5" data-testid={testId}>
    <p className="label-mono mb-3">{title}</p>
    {rows.length === 0 && <p className="text-xs text-slate-500">Not enough data.</p>}
    {rows.map((r, i) => (
      <div key={r.id} className="flex items-center gap-3 py-1.5 border-b border-slate-800/50 last:border-0">
        <span className="font-mono text-xs text-slate-500 w-4">{i + 1}</span>
        <span className="text-sm text-slate-100 flex-1 truncate">{r.id}</span>
        {render(r)}
      </div>
    ))}
  </div>
);

const printLight = (pack) => {
  const el = document.documentElement, wasDark = !el.classList.contains("light");
  el.classList.add("light");
  el.classList.toggle("board-pack", !!pack);
  setTimeout(() => { window.print(); if (wasDark) el.classList.remove("light"); el.classList.remove("board-pack"); }, 400);
};

export const ExecSummary = ({ raw }) => {
  const s = useMemo(() => {
    const cfg = { ...DEFAULT_SHEET, holidays: raw.holidays, overrides: raw.shiftOverrides };
    const fm = fullMonths(raw.months, lastDataDay(raw));
    const { perUser, team } = teamMonthly(raw, raw.months, cfg);
    const teamFull = team.filter((t) => fm.includes(t.key));
    const cur = teamFull[teamFull.length - 1], prev = teamFull[teamFull.length - 2];
    const quarters = toQuarters(team);
    const fq = quarters.filter((q) => q.months === 3);
    const qCur = fq[fq.length - 1], qPrev = fq[fq.length - 2];
    const userQ = perUser.map(({ id, rows }) => ({ id, q: toQuarters(rows).find((q) => q.key === qCur?.key) })).filter((x) => x.q && x.q.hrs >= 50);
    const ranked = [...userQ].sort((a, b) => b.q.vph - a.q.vph);
    const movers = qCur && qPrev ? quarterReview(perUser, qPrev.key, qCur.key, "vph") : [];
    const qMonths = team.filter((t) => qCur && toQuarters([t])[0].key === qCur.key).map((t) => t.key);
    const peak = qMonths.length ? peakGapHour(raw, qMonths[0], qMonths[qMonths.length - 1], cfg) : null;
    const lowDays = prev ? perUser.filter(({ rows }) => { const r = rows.find((x) => x.key === cur.key); return r && r.days > 0 && r.days < 15; }).length : 0;
    return { cfg, team: teamFull, cur, prev, quarters, qCur, qPrev, top: ranked.slice(0, 5), bottom: ranked.slice(-5).reverse(), movers, peak, lowDays };
  }, [raw]);
  const { cur, prev, qCur, qPrev } = s;
  if (!cur) return <p className="p-10 text-slate-400">Not enough data for a summary.</p>;
  const d = (k) => pctChange(cur[k], prev?.[k]);
  const lostPct = qCur ? (qCur.lost / (qCur.days * 8 * 3600)) * 100 : 0;
  const insights = [
    qCur && qPrev && `Team visits per hour ${qCur.vph >= qPrev.vph ? "rose" : "fell"} ${Math.abs(pctChange(qCur.vph, qPrev.vph)).toFixed(1)}% from ${qPrev.key} to ${qCur.key} (${fmtNum(qPrev.vph)} → ${fmtNum(qCur.vph)}).`,
    qCur && qPrev && `Lost time per picker-day ${qCur.lostDay <= qPrev.lostDay ? "improved" : "worsened"} to ${fmtDur(qCur.lostDay)} (was ${fmtDur(qPrev.lostDay)}), about ${lostPct.toFixed(0)}% of scheduled shift time in ${qCur.key}.`,
    s.peak && `Most idle time happens ${String(s.peak.hour).padStart(2, "0")}:00–${String(s.peak.hour + 1).padStart(2, "0")}:00 (${fmtDur(s.peak.minutes * 60)} across the team in ${qCur.key}).`,
    s.movers[0]?.change > 0 && `Most improved: ${s.movers[0].id} (▲ ${s.movers[0].change.toFixed(1)}% visits/hr).`,
    s.movers[s.movers.length - 1]?.change < 0 && `Biggest decline: ${s.movers[s.movers.length - 1].id} (▼ ${Math.abs(s.movers[s.movers.length - 1].change).toFixed(1)}% visits/hr).`,
    `${cur.active} pickers active in ${monthLabel(cur.key)}; ${s.lowDays} worked fewer than 15 days (possible leave).`,
  ].filter(Boolean);

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-10 space-y-8 print-area" data-testid="exec-summary">
      <PrintHeader title="Executive summary" subtitle={`${monthLabel(cur.key)} vs ${prev ? monthLabel(prev.key) : "—"} · ${raw.fileName}`} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-mono mb-2">Executive summary</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-100" data-testid="summary-title">{monthLabel(cur.key)} performance</h1>
          <p className="text-sm text-slate-400 mt-2">vs {prev ? monthLabel(prev.key) : "—"} · latest full quarter {qCur?.key || "—"} · {raw.fileName}</p>
        </div>
        <div className="no-print flex gap-2">
        <button data-testid="summary-board-pack" onClick={() => printLight(true)} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800"><FileText className="h-3.5 w-3.5" /> Board pack (2-page PDF)</button>
        <button data-testid="summary-print" onClick={() => printLight(false)} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-ep hover:bg-ep-hover text-white"><Printer className="h-3.5 w-3.5" /> Print / PDF</button>
        </div>
      </div>

      <div className="panel p-6" data-testid="summary-insights">
        <p className="label-mono mb-3">Key points</p>
        <ul className="space-y-2">{insights.map((t, i) => <li key={i} className="text-sm text-slate-200 flex gap-3"><span className="text-ep-soft font-mono">{String(i + 1).padStart(2, "0")}</span>{t}</li>)}</ul>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Visits" value={fmtNum(cur.visits)} d={d("visits")} testId="summary-kpi-visits" />
        <Kpi label="Qty picked" value={fmtNum(cur.qty)} d={d("qty")} testId="summary-kpi-qty" />
        <Kpi label="Hours worked" value={fmtNum(cur.hrs)} d={d("hrs")} testId="summary-kpi-hours" />
        <Kpi label="Active pickers" value={cur.active} d={pctChange(cur.active, prev?.active)} testId="summary-kpi-active" />
        <Kpi label="Visits / hr" value={fmtNum(cur.vph)} d={d("vph")} testId="summary-kpi-vph" />
        <Kpi label="Qty / hr" value={fmtNum(cur.qph)} d={d("qph")} testId="summary-kpi-qph" />
        <Kpi label="Lost / picker-day" value={fmtDur(cur.lostDay)} d={d("lostDay")} inverse testId="summary-kpi-lost" />
        <Kpi label="Absent days" value={fmtNum(cur.absent)} d={d("absent")} inverse testId="summary-kpi-absent" />
      </div>

      <HistoryCompare team={s.team} />
      <div data-pack="break"><HeadcountEfficiency team={s.team} holidays={raw.holidays} /></div>
      <div className="panel p-5 sm:p-6" data-testid="summary-commentary">
        <NotesPanel title="Commentary · last 12 months" start={monthBounds(s.team.slice(-12)[0]?.key || cur.key).start} end={monthBounds(cur.key).end} defaults={{ period: "month", value: cur.key }} pickers={raw.users.map((u) => u.id)} testId="summary-notes" limit={6} empty="No commentary yet. Add notes to explain trends for the board." />
      </div>

      <div className="panel p-5 sm:p-6" data-testid="summary-trend" data-pack="hide">
        <p className="label-mono mb-4">Monthly efficiency trend</p>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height={280} initialDimension={{ width: 900, height: 280 }}>
            <ComposedChart data={s.team.map((t) => ({ label: monthLabel(t.key), vph: +(t.vph || 0).toFixed(1), qph: +(t.qph || 0).toFixed(1), active: t.active }))}>
              <CartesianGrid stroke="#1F2937" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis yAxisId="q" stroke="#10B981" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
              <YAxis yAxisId="v" orientation="right" stroke="#D4333B" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
              <YAxis yAxisId="a" hide tickFormatter={fmtCompact} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="a" dataKey="active" name="Active pickers" fill="#334155" radius={[3, 3, 0, 0]} maxBarSize={26} />
              <Line yAxisId="v" dataKey="vph" name="Visits / hr" stroke="#D4333B" strokeWidth={2.5} dot={false} />
              <Line yAxisId="q" dataKey="qph" name="Qty / hr" stroke="#10B981" strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel p-5 sm:p-6" data-testid="summary-quarters" data-pack="hide">
        <p className="label-mono mb-3">Team quarter scorecard</p>
        <QuarterCompare quarters={s.quarters} hasQty={raw.hasQty} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <List title={`Top visits/hr · ${qCur?.key}`} rows={s.top} testId="summary-top" render={(r) => <span className="font-mono text-xs text-emerald-400">{fmtNum(r.q.vph)}</span>} />
        <List title={`Lowest visits/hr · ${qCur?.key}`} rows={s.bottom} testId="summary-bottom" render={(r) => <span className="font-mono text-xs text-rose-400">{fmtNum(r.q.vph)}</span>} />
        <List title="Most improved (QoQ)" rows={s.movers.filter((m) => m.change > 0).slice(0, 5)} testId="summary-improved" render={(r) => <Delta v={r.change} />} />
        <List title="Biggest decline (QoQ)" rows={s.movers.filter((m) => m.change < 0).reverse().slice(0, 5)} testId="summary-declined" render={(r) => <Delta v={r.change} />} />
      </div>
      <p className="text-[11px] text-slate-500" data-pack="hide">Uses default settings: shifts Early 06–14 / Late 14–22 / Night 22–06 detected from first pick, large gap ≥ {s.cfg.gapMin} min, {s.cfg.breakMin} min lunch. Rankings need 50+ hours in the quarter. Hidden pickers and holidays are excluded.</p>
    </div>
  );
};
